import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EventAssociationLink } from '@/types/events';
import { toast } from 'sonner';

export function useEventAssociationLinks(eventId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: links = [], isLoading } = useQuery({
    queryKey: ['event-association-links', eventId],
    queryFn: async () => {
      if (!eventId) return [];
      const { data, error } = await supabase
        .from('os_event_association_links')
        .select(`
          id,
          event_id,
          association_id,
          association_type_id,
          created_at,
          association:os_events_associations(id, name, logo_url),
          association_type:os_event_association_types(id, name, label)
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      const raw = data as Record<string, unknown>[] | null;
      return (raw ?? []).map((row: Record<string, unknown>) => ({
        id: row.id,
        event_id: row.event_id,
        association_id: row.association_id,
        association_type_id: row.association_type_id,
        created_at: row.created_at,
        association: (row.association ?? row.os_events_associations) as EventAssociationLink['association'],
        association_type: (row.association_type ?? row.os_event_association_types) as EventAssociationLink['association_type'],
      })) as EventAssociationLink[];
    },
    enabled: !!eventId,
  });

  const setLinks = useMutation({
    mutationFn: async ({
      eventId: eid,
      links: linkRows,
    }: {
      eventId: string;
      links: { association_id: string; association_type_id: string }[];
    }) => {
      const { error: delError } = await supabase
        .from('os_event_association_links')
        .delete()
        .eq('event_id', eid);
      if (delError) throw delError;
      if (linkRows.length === 0) return [];
      const rows = linkRows.map(({ association_id, association_type_id }) => ({
        event_id: eid,
        association_id,
        association_type_id,
      }));
      const { data, error } = await supabase
        .from('os_event_association_links')
        .insert(rows as never)
        .select('id, event_id, association_id, association_type_id, created_at');
      if (error) throw error;
      return data ?? [];
    },
    onSuccess: (_, { eventId: eid }) => {
      queryClient.invalidateQueries({ queryKey: ['event-association-links', eid] });
      queryClient.invalidateQueries({ queryKey: ['event', eid] });
      toast.success('Associations updated');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Failed to update associations'),
  });

  const associationIds = links.map((l) => l.association_id);

  return {
    links,
    associationIds,
    isLoading,
    setLinks: (linkRows: { association_id: string; association_type_id: string }[]) =>
      eventId ? setLinks.mutateAsync({ eventId, links: linkRows }) : Promise.reject(new Error('No event')),
    setLinksMutation: setLinks,
  };
}
